import { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { BrushHighlight } from '@/components/BrushHighlight';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { AuthFormLayout } from '@/components/auth/AuthFormLayout';
import { useAuthForm } from '@/hooks/useAuthForm';
import { useAuthFeatureFlags } from '@/hooks/useAuthFeatureFlags';
import { authService } from '@/lib/services/authService';
import { analyticsService } from '@/lib/services/analyticsService';
import { supabase } from '@/integrations/supabase/client';
import { translateAuthError, isEmailNotConfirmedError } from '@/lib/utils/authErrors';
import { audit } from '@/lib/services/auditService';
import { getEdgeFunctionUrl } from '@/lib/config/environment';
import { getAuthCallbackUrl } from '@/lib/utils/authRedirect';
import { toast } from 'sonner';


const SCREEN_ID = 'AUTH-101';
const PENDING_CREDENTIALS_KEY = 'pending_email_confirmation';

interface LocationState {
  email?: string;
  password?: string;
  error?: string;
}

const Login = () => {
  const navigate = useSafeNavigate();
  const location = useLocation();
  const hasEmittedView = useRef(false);
  const hasRestoredCredentials = useRef(false);

  const {
    email,
    password,
    emailError,
    passwordError,
    isEmailFocused,
    isPasswordFocused,
    handleEmailChange,
    handleEmailBlur,
    handleEmailFocus,
    handlePasswordChange,
    handlePasswordBlur,
    handlePasswordFocus,
    validateEmail,
    validatePassword,
    setEmailError,
    setPasswordError,
    ripple,
    buttonRef,
    triggerRipple,
    resetRipple,
    setEmail,
    setPassword,
  } = useAuthForm({
    emailErrorMessage: 'Неверный формат email',
    passwordErrorMessage: 'Минимум 8 символов, буквы и цифры',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isVkLoading, setIsVkLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Use unified auth feature flags hook
  const { showVkAuth: isVkEnabled, showGoogleAuth: isGoogleEnabled } = useAuthFeatureFlags();

  // Restore email and password from navigation state or sessionStorage
  useEffect(() => {
    if (hasRestoredCredentials.current) return;
    hasRestoredCredentials.current = true;
    
    const state = location.state as LocationState | null;
    
    // Try state first, then sessionStorage
    let restoredEmail = state?.email;
    let restoredPassword = state?.password;
    
    if (!restoredEmail) {
      const stored = sessionStorage.getItem(PENDING_CREDENTIALS_KEY);
      if (stored) {
        try {
          const creds = JSON.parse(stored);
          restoredEmail = creds.email;
          restoredPassword = creds.password;
        } catch {
          // Ignore parse errors
        }
      }
    }
    
    if (restoredEmail) {
      setEmail(restoredEmail);
    }
    if (restoredPassword) {
      setPassword(restoredPassword);
    }
  }, [location.state, setEmail, setPassword]);

  useEffect(() => {
    if (hasEmittedView.current) return;
    hasEmittedView.current = true;
    
    analyticsService.emit({
      event_type: 'auth_login_view',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
    });
    
    // Check for auth error from ultra-early redirect (sessionStorage)
    const storedErrorCode = sessionStorage.getItem('auth_callback_error');
    if (storedErrorCode) {
      sessionStorage.removeItem('auth_callback_error');
      
      const errorMessages: Record<string, string> = {
        'otp_expired': 'Ссылка устарела. Запросите новую.',
        'access_denied': 'Доступ запрещён',
        'invalid_request': 'Некорректный запрос',
        'email_not_confirmed': 'Email не подтверждён',
        'user_not_found': 'Пользователь не найден',
        'invalid_grant': 'Недействительный код авторизации',
      };
      
      const message = errorMessages[storedErrorCode] || 'Ошибка авторизации';
      toast.error(message);
    }
    
    // Show error toast if redirected from auth callback with error (fallback)
    const state = location.state as LocationState | null;
    if (state?.error) {
      toast.error(state.error);
      // Clear the error from state to prevent showing again on re-render
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const isButtonEnabled = email.length > 0 && password.length > 0 && !isLoading && !isGoogleLoading && !emailError && !passwordError;

  const handleSubmit = useCallback(async () => {
    if (!isButtonEnabled) return;
    
    if (!validateEmail(email)) {
      setEmailError('Неверный формат email');
      return;
    }

    if (!validatePassword(password)) {
      setPasswordError('Минимум 8 символов, буквы и цифры');
      return;
    }
    
    setIsLoading(true);
    setApiError(null);
    
    analyticsService.emit({
      event_type: 'auth_login_submit',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
      payload: { method: 'password' },
    });

    try {
      const { user, error } = await authService.signIn(email, password);
      
      if (error || !user) {
        // Check if email not confirmed error - redirect to confirmation screen
        if (isEmailNotConfirmedError(error)) {
          analyticsService.emit({
            event_type: 'auth_login_error',
            screen_id: SCREEN_ID,
            timestamp: analyticsService.timestamp(),
            payload: { method: 'password', error_code: 'email_not_confirmed' },
          });
          
          setIsLoading(false);
          navigate('/auth/email-confirmation', { 
            state: { email, password }, 
            replace: true 
          });
          return;
        }
        
        const errorMessage = translateAuthError(error);
        setApiError(errorMessage);
        
        analyticsService.emit({
          event_type: 'auth_login_error',
          screen_id: SCREEN_ID,
          timestamp: analyticsService.timestamp(),
          payload: { method: 'password', error_code: 'auth_failed' },
        });
        
        setIsLoading(false);
        return;
      }

      // Clear pending credentials on successful login
      sessionStorage.removeItem(PENDING_CREDENTIALS_KEY);

      analyticsService.emit({
        event_type: 'auth_login_success',
        screen_id: SCREEN_ID,
        timestamp: analyticsService.timestamp(),
        payload: { method: 'password' },
      });

      // Audit: log successful login
      if (user?.id) {
        audit.userLoggedIn(user.id, 'password');
      }

      setIsLoading(false);
      setTimeout(() => {
        navigate('/catalog', { replace: true });
      }, 300);
    } catch (err) {
      const errorMessage = translateAuthError(err instanceof Error ? err : null);
      setApiError(errorMessage);
      setIsLoading(false);
    }
  }, [isButtonEnabled, email, password, navigate, setEmailError, setPasswordError, validateEmail, validatePassword]);

  const handleGoogleSignIn = useCallback(async () => {
    if (isLoading || isGoogleLoading || isVkLoading) return;
    
    setIsGoogleLoading(true);
    setApiError(null);
    
    analyticsService.emit({
      event_type: 'auth_login_submit',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
      payload: { method: 'google' },
    });

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthCallbackUrl(),
          skipBrowserRedirect: false,
        },
      });

      if (error) {
        setApiError(translateAuthError(error));
        analyticsService.emit({
          event_type: 'auth_login_error',
          screen_id: SCREEN_ID,
          timestamp: analyticsService.timestamp(),
          payload: { 
            method: 'google',
            error_code: 'oauth_failed',
          },
        });
        setIsGoogleLoading(false);
      }
    } catch (err) {
      setApiError(translateAuthError(err instanceof Error ? err : 'Произошла ошибка при входе через Google'));
      setIsGoogleLoading(false);
    }
  }, [isLoading, isGoogleLoading, isVkLoading]);

  const handleVkSignIn = useCallback(async () => {
    if (isLoading || isGoogleLoading || isVkLoading) return;
    
    setIsVkLoading(true);
    setApiError(null);
    
    analyticsService.emit({
      event_type: 'auth_login_submit',
      screen_id: SCREEN_ID,
      timestamp: analyticsService.timestamp(),
      payload: { method: 'vk' },
    });

    try {
      // Use custom VK OAuth edge function
      const redirectUri = `${window.location.origin}/auth/callback?provider=vk`;
      const vkAuthUrl = `${getEdgeFunctionUrl('auth-vk')}?action=init&redirect_uri=${encodeURIComponent(redirectUri)}`;
      
      // Redirect to VK OAuth
      window.location.href = vkAuthUrl;
    } catch (err) {
      setApiError(translateAuthError(err instanceof Error ? err : 'Произошла ошибка при входе через VK'));
      setIsVkLoading(false);
    }
  }, [isLoading, isGoogleLoading, isVkLoading]);

  const handleBack = useCallback(() => {
    navigate('/onboarding/role');
  }, [navigate]);

  const handleForgotPassword = useCallback(() => {
    navigate('/auth/forgot-password', { state: { email } });
  }, [navigate, email]);

  const handleRegister = useCallback(() => {
    navigate('/auth/signup', { state: { email } });
  }, [navigate, email]);

  const headerContent = (
    <div className="mt-4 mb-6">
      <h1 
        className="text-text-primary"
        style={{
          fontFamily: 'CoFo Gothic, sans-serif',
          fontWeight: 900,
          fontSize: '32px',
          lineHeight: '100%',
        }}
      >
        с возвращением!
        <br />
        рады вас <BrushHighlight>видеть</BrushHighlight>
      </h1>
    </div>
  );

  const formFields = (
    <>
      <div className="space-y-2">
        <label 
          htmlFor="email"
          className="block text-text-primary"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontWeight: 700,
            fontSize: '14px',
          }}
        >
          Ваша почта
        </label>
        <div className="relative">
          <input
            data-testid="auth-101-input-email"
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={handleEmailChange}
            onFocus={handleEmailFocus}
            onBlur={handleEmailBlur}
            disabled={isLoading || isGoogleLoading}
            placeholder="Почта"
            className={`
              w-full bg-bg-secondary text-text-primary placeholder:text-text-tertiary
              outline-none transition-all
              ${emailError && !isEmailFocused ? 'ring-2 ring-accent-pink' : ''}
            `}
            style={{
              height: '52px',
              borderRadius: '16px',
              padding: '0 16px',
              fontFamily: 'CoFo Gothic, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
            }}
          />
        </div>
        {emailError && !isEmailFocused && (
          <p 
            data-testid="auth-101-email-error"
            className="text-text-accent-pink"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {emailError}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label 
          htmlFor="password"
          className="block text-text-primary"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontWeight: 700,
            fontSize: '14px',
          }}
        >
          Ваш пароль
        </label>
        <div className="relative">
          <input
            data-testid="auth-101-input-password"
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            value={password}
            onChange={handlePasswordChange}
            onFocus={handlePasswordFocus}
            onBlur={handlePasswordBlur}
            disabled={isLoading || isGoogleLoading}
            placeholder="Пароль"
            className={`
              w-full bg-bg-secondary text-text-primary placeholder:text-text-tertiary outline-none
              ${passwordError && !isPasswordFocused ? 'ring-2 ring-accent-pink' : ''}
            `}
            style={{
              height: '52px',
              borderRadius: '16px',
              padding: '0 48px 0 16px',
              fontFamily: 'CoFo Gothic, sans-serif',
              fontWeight: 400,
              fontSize: '16px',
            }}
          />
          <button
            data-testid="auth-101-toggle-password"
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-text-tertiary"
            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
          >
            {showPassword ? (
              <EyeOff className="w-5 h-5" />
            ) : (
              <Eye className="w-5 h-5" />
            )}
          </button>
        </div>
        {passwordError && !isPasswordFocused && (
          <p 
            data-testid="auth-101-password-error"
            className="text-text-accent-pink"
            style={{
              fontFamily: 'CoFo Gothic, sans-serif',
              fontSize: '12px',
              marginTop: '4px',
            }}
          >
            {passwordError}
          </p>
        )}
      </div>

      {/* API error message */}
      {apiError && (
        <p 
          data-testid="auth-101-api-error"
          className="text-text-accent-pink text-center"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontSize: '14px',
          }}
        >
          {apiError}
        </p>
      )}
    </>
  );

  const isOAuthDisabled = isLoading || isGoogleLoading || isVkLoading;

  const afterAction = (
    <div className="space-y-4">

      <div className="flex items-center justify-center py-2">
        <span 
          className="text-text-tertiary"
          style={{
            fontFamily: 'CoFo Gothic, sans-serif',
            fontSize: '14px',
          }}
        >
          или войти через
        </span>
      </div>

      <button
        data-testid="auth-101-cta-google"
        onClick={handleGoogleSignIn}
        disabled={isOAuthDisabled}
        className="w-full flex items-center justify-center gap-3 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        style={{
          height: '52px',
          borderRadius: '24px',
          fontFamily: 'CoFo Gothic, sans-serif',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        {isGoogleLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Войти через Google
          </>
        )}
      </button>

      {isVkEnabled && (
        <button
          data-testid="auth-101-cta-vk"
          onClick={handleVkSignIn}
          disabled={isOAuthDisabled}
          className="w-full flex items-center justify-center gap-3 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          style={{
            height: '52px',
            borderRadius: '24px',
            fontFamily: 'CoFo Gothic, sans-serif',
            fontWeight: 600,
            fontSize: '16px',
          }}
        >
          {isVkLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4C75A3" d="M12.785 16.241s.288-.032.436-.194c.136-.148.132-.427.132-.427s-.02-1.304.576-1.496c.588-.19 1.341 1.26 2.14 1.818.605.422 1.064.33 1.064.33l2.137-.03s1.117-.071.587-.964c-.043-.073-.308-.661-1.588-1.87-1.34-1.264-1.16-1.059.453-3.246.983-1.332 1.376-2.145 1.253-2.493-.117-.332-.84-.244-.84-.244l-2.406.015s-.178-.025-.31.056c-.13.079-.212.263-.212.263s-.382 1.03-.89 1.906c-1.07 1.843-1.5 1.94-1.675 1.826-.407-.267-.305-1.07-.305-1.642 0-1.784.267-2.528-.52-2.72-.26-.063-.452-.105-1.12-.112-.857-.009-1.582.003-1.992.207-.273.136-.484.44-.356.457.158.022.516.098.706.36.245.34.237 1.1.237 1.1s.14 2.098-.328 2.358c-.321.178-.762-.185-1.71-1.85-.486-.852-.853-1.794-.853-1.794s-.07-.178-.197-.273c-.153-.115-.368-.151-.368-.151l-2.286.015s-.343.01-.47.161c-.112.135-.009.413-.009.413s1.79 4.258 3.817 6.403c1.858 1.967 3.968 1.838 3.968 1.838h.956z"/>
              </svg>
              Войти через VK
            </>
          )}
        </button>
      )}
    </div>
  );

  const footer = (
    <span 
      className="text-text-primary"
      style={{
        fontFamily: 'CoFo Gothic, sans-serif',
        fontSize: '14px',
      }}
    >
      Нет аккаунта?{' '}
      <button
        data-testid="auth-101-link-register"
        onClick={handleRegister}
        className="underline font-bold"
      >
        Зарегистрироваться
      </button>
    </span>
  );

  return (
    <AuthFormLayout
      testId="auth-101-screen"
      screenId={SCREEN_ID}
      onBack={handleBack}
      backTestId="auth-101-back"
      rightAction={{
        label: 'Забыли пароль?',
        onClick: handleForgotPassword,
        testId: 'auth-101-link-forgot',
      }}
      headerContent={headerContent}
      error={apiError || undefined}
      errorTestId="auth-101-error-block"
      primaryAction={{
        label: 'Продолжить',
        onClick: handleSubmit,
        disabled: !isButtonEnabled,
        loading: isLoading,
        testId: 'auth-101-cta-primary',
        buttonRef,
        ripple,
        onPointerDown: triggerRipple,
        onRippleEnd: resetRipple,
      }}
      afterAction={afterAction}
      footer={footer}
    >
      {formFields}
    </AuthFormLayout>
  );
};

export default Login;
